import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Pencil, Plus, Upload, X, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import AdminCourses from "./AdminCourses";
import AdminDashboard from "./AdminDashboard";
import AdminSocial from "./AdminSocial";
import AdminServices from "./AdminServices";
import AdminMarketing from "./AdminMarketing";
import AdminInvites from "./AdminInvites";
import AdminPremium from "./AdminPremium";
import AdminIdeas from "./AdminIdeas";
import AdminTasks from "./AdminTasks";
import { uploadFile } from "./upload";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_FIELDS = {
  reels: [
    { name: "title", label: "Title", type: "text" },
    { name: "src", label: "Video URL (YouTube link or upload — up to ~100MB)", type: "text", required: true, upload: true },
  ],
  updates: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "category", label: "Category", type: "category-select" },
    { name: "slug", label: "Slug (auto if empty)", type: "text" },
    { name: "date", label: "Date (YYYY-MM-DD)", type: "text" },
    { name: "excerpt", label: "Excerpt", type: "textarea" },
    { name: "body", label: "Body — paragraphs separated by blank lines; '## ' for subheadings; '- ' for bullets", type: "textarea", rows: 12, required: true },
  ],
  downloads: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea" },
    { name: "href", label: "File URL (or upload)", type: "text", required: true, upload: true },
    { name: "size", label: "Size label (e.g. PDF · 2 pages)", type: "text" },
  ],
  categories: [
    { name: "name", label: "Category name (shown as a filter chip under Updates)", type: "text", required: true },
  ],
};
const KINDS = ["reels", "updates", "downloads", "categories", "modules", "social"];

function ItemForm({ kind, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [cats, setCats] = useState([]);
  const [aiIdea, setAiIdea] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [useIdeas, setUseIdeas] = useState(false);
  const [recentIdeas, setRecentIdeas] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aiDraft = async () => {
    if (!aiIdea.trim() && !useIdeas) return toast.error("Type the update idea or enable 'Use Ideas'");
    setAiBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/tribe/ai/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ prompt: aiIdea.trim(), use_ideas: useIdeas }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "AI drafting failed");
      setForm((f) => ({ ...f, title: d.title, excerpt: d.excerpt, body: d.body }));
      toast.success("Draft ready — edit anything before saving");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiBusy(false);
    }
  };

  useEffect(() => {
    if (!KIND_FIELDS[kind].some((f) => f.type === "category-select")) return;
    fetch(`${API}/api/admin/tribe/categories`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setCats)
      .catch(() => {});
  }, [kind]);

  // Load recent ideas for suggestions
  useEffect(() => {
    if (kind !== "updates") return;
    fetch(`${API}/api/admin/ideas?limit=6`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => setRecentIdeas(d.items || []))
      .catch(() => {});
  }, [kind]);

  const doUpload = async (field, file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const href = await uploadFile(file, setProgress);
      set(field, href);
      toast.success("File uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const save = async () => {
    for (const f of KIND_FIELDS[kind]) if (f.required && !form[f.name]?.trim()) return toast.error(`${f.label.split(" (")[0]} is required`);
    setBusy(true);
    const isEdit = Boolean(initial?.id);
    try {
      const r = await fetch(`${API}/api/admin/tribe/${kind}${isEdit ? `/${initial.id}` : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      toast.success(isEdit ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-foam bg-white/70 p-6 md:p-8 mb-6" data-testid={`admin-form-${kind}`}>
      <div className="flex items-center justify-between mb-5">
        <p className="rp-display text-xl font-medium text-[#262016]">{initial?.id ? "Edit item" : "Add new item"}</p>
        <button onClick={onCancel} className="text-[#8A7E6B] hover:text-[#262016]" data-testid="admin-form-cancel"><X size={18} /></button>
      </div>
      {kind === "updates" && (
        <div className="mb-6 p-4 rounded-xl" style={{ border: "1px solid rgba(181,121,63,0.35)", background: "rgba(181,121,63,0.06)" }}>
          <label className="block text-[11px] uppercase tracking-[0.14em] text-[#B5793F] font-semibold mb-1.5">
            <Sparkles size={11} className="inline mr-1" />AI assistant — describe the update, it drafts title, excerpt &amp; body
          </label>
          <div className="flex gap-2">
            <input value={aiIdea} onChange={(e) => setAiIdea(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && aiDraft()}
                   placeholder={useIdeas ? "Optional: guide the AI or leave blank to use ideas only" : "e.g. 3 Midjourney tricks for consistent brand characters"}
                   data-testid="ai-update-prompt"
                   className="flex-1 rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]" />
            <button onClick={aiDraft} disabled={aiBusy} data-testid="ai-update-btn"
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-[#F6F1E5] disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
              <Sparkles size={13} /> {aiBusy ? "Drafting…" : "Draft"}
            </button>
          </div>

          {/* Ideas toggle and suggestion chips */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setUseIdeas(!useIdeas)}
              data-testid="ai-use-ideas-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all"
              style={{
                background: useIdeas ? "#B5793F20" : "transparent",
                color: useIdeas ? "#B5793F" : "#8A7E6B",
                border: `1px solid ${useIdeas ? "#B5793F55" : "#26201615"}`,
              }}>
              <Lightbulb size={11} /> {useIdeas ? "Using Ideas" : "Use my Ideas"}
            </button>

            {recentIdeas.length > 0 && (
              <>
                <span className="text-[10px] text-[#8A7E6B]">Quick:</span>
                {recentIdeas.slice(0, 4).map((idea) => (
                  <button
                    key={idea.id}
                    onClick={() => { setAiIdea(idea.text.substring(0, 100)); setUseIdeas(true); }}
                    data-testid={`idea-chip-${idea.id}`}
                    className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-[#26201608] text-[#5C5042] border border-[#26201610] hover:border-[#B5793F55] hover:bg-[#B5793F08] transition-colors truncate max-w-[180px]"
                    title={idea.text}>
                    {idea.text.substring(0, 50)}{idea.text.length > 50 ? "…" : ""}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
      <div className="space-y-4">
        {KIND_FIELDS[kind].map((f) => (
          <div key={f.name}>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5">{f.label}</label>
            <div className="flex gap-2">
              {f.type === "textarea" ? (
                <textarea value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} rows={f.rows || 3}
                          data-testid={`admin-field-${f.name}`}
                          className="flex-1 rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]" />
              ) : f.type === "category-select" ? (
                <select value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                        data-testid={`admin-field-${f.name}`}
                        className="flex-1 rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]">
                  <option value="">No category</option>
                  {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              ) : (
                <input value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                       data-testid={`admin-field-${f.name}`}
                       className="flex-1 rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]" />
              )}
              {f.upload && (
                <label className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#26201625] text-sm text-[#5C5042] cursor-pointer hover:border-[#262016]">
                  <Upload size={14} /> {progress === null ? "Upload" : `${progress}%`}
                  <input type="file" className="hidden" data-testid={`admin-upload-${f.name}`}
                         onChange={(e) => { doUpload(f.name, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="admin-form-save"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
        {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

export default function TribeAdmin() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(() => {
    try { return sessionStorage.getItem("tribe_admin") === "true" ? "admin" : "checking"; }
    catch { return "checking"; }
  });
  const [kind, setKind] = useState("reels");
  const [section, setSection] = useState("dashboard");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | item

  useEffect(() => {
    if (status === "admin") return;
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => {
        const s = u.is_admin ? "admin" : "denied";
        setStatus(s);
        try { sessionStorage.setItem("tribe_admin", u.is_admin ? "true" : "false"); } catch {}
      })
      .catch(() => { setStatus("denied"); sessionStorage.removeItem("tribe_admin"); });
  }, []);

  const load = useCallback(() => {
    if (kind === "modules" || kind === "social") return;
    fetch(`${API}/api/admin/tribe/${kind}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setItems)
      .catch(() => setItems([]));
  }, [kind]);

  useEffect(() => { if (status === "admin") load(); }, [status, kind, load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    const r = await fetch(`${API}/api/admin/tribe/${kind}/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); load(); } else toast.error("Delete failed");
  };

  if (status === "checking")
    return (
      <div className="min-h-screen" style={{ background: "#EDE7D9" }}>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
          <div className="h-5 w-32 bg-[#26201615] rounded-full mb-8 animate-pulse" />
          <div className="h-12 w-64 bg-[#26201612] rounded-xl mb-8 animate-pulse" />
          <div className="flex gap-4 mb-8">
            {[1,2,3,4,5].map(i => <div key={i} className="h-11 w-28 bg-[#26201610] rounded-xl animate-pulse" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-32 bg-white/40 rounded-2xl animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  if (status === "denied")
    return <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "#EDE7D9" }} data-testid="admin-denied">
      <p className="rp-display text-2xl text-[#262016]">Admins only.</p>
      <button onClick={() => navigate("/tribe")} className="text-sm underline text-[#5C5042]">Back to the Tribe</button></div>;

  return (
    <div className="min-h-screen" style={{ background: "#EDE7D9" }} data-testid="tribe-admin-page">
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 md:py-14">
        <button onClick={() => navigate("/tribe")} data-testid="admin-back-btn"
                className="inline-flex items-center gap-2 mb-8 text-sm font-medium text-[#5C5042] hover:text-[#262016]">
          <ArrowLeft size={15} /> Back to the Tribe
        </button>
        <h1 className="rp-display text-4xl sm:text-5xl font-light text-[#262016] mb-8">Tribe Admin</h1>

        <div className="flex gap-3 mb-8 overflow-x-auto pb-2 scrollbar-hide" data-testid="admin-sections" style={{ WebkitOverflowScrolling: "touch" }}>
          {["dashboard", "content", "services", "marketing", "growth", "premium", "ideas", "tasks"].map((s) => (
            <button key={s} onClick={() => setSection(s)} data-testid={`admin-section-${s}`}
                    className="shrink-0 px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-[0.1em] transition-all border-2"
                    style={{
                      background: s === section ? "linear-gradient(135deg, #262016 0%, #3B301F 100%)" : "rgba(255,255,255,0.5)",
                      color: s === section ? "#EDE7D9" : "#262016",
                      borderColor: s === section ? "#B5793F" : "rgba(38,32,22,0.15)",
                    }}>
              {s}
            </button>
          ))}
        </div>

        {section === "dashboard" ? (
          <AdminDashboard />
        ) : section === "services" ? (
          <AdminServices />
        ) : section === "marketing" ? (
          <AdminMarketing />
        ) : section === "growth" ? (
          <AdminInvites />
        ) : section === "premium" ? (
          <AdminPremium />
        ) : section === "ideas" ? (
          <AdminIdeas />
        ) : section === "tasks" ? (
          <AdminTasks />
        ) : (
          <>
        <nav className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {KINDS.map((k) => (
            <button key={k} onClick={() => { setKind(k); setEditing(null); }} data-testid={`admin-tab-${k}`}
                    className="px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all"
                    style={{
                      background: k === kind ? "#262016" : "transparent",
                      color: k === kind ? "#EDE7D9" : "#262016",
                      border: "1px solid " + (k === kind ? "#262016" : "rgba(38,32,22,0.2)"),
                    }}>
              {k}
            </button>
          ))}
        </nav>

        {kind === "modules" ? (
          <AdminCourses />
        ) : kind === "social" ? (
          <AdminSocial />
        ) : (
          <>
            {editing !== null ? (
              <ItemForm kind={kind} initial={editing.id ? editing : null}
                        onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : (
              <button onClick={() => setEditing({})} data-testid="admin-add-btn"
                      className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5]"
                      style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
                <Plus size={15} /> Add {kind.slice(0, -1)}
              </button>
            )}

            <div data-testid="admin-items-list">
              {items.length === 0 && <p className="rp-foam bg-white/55 p-6 text-sm text-[#8A7E6B]">No items yet.</p>}
              {items.map((it) => (
                <div key={it.id} className="bg-white rounded-2xl flex items-center gap-4 px-5 py-4 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }} data-testid={`admin-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                <p className="rp-display text-base md:text-lg font-medium text-[#262016] truncate">{it.title || it.name || it.src}</p>
                    <p className="text-xs text-[#8A7E6B] truncate">{it.slug || it.href || it.src || ""} {it.date ? `· ${it.date}` : ""}</p>
                  </div>
                  <button onClick={() => setEditing(it)} data-testid={`admin-edit-${it.id}`} title="Edit"
                          className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016] hover:border-[#262016]">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`} title="Delete"
                          className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
          </>
        )}
      </main>
    </div>
  );
}
